import { CSSProperties, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project import
import MainCard from 'components/MainCard';
import { breadcrumbRoutes } from 'config/breadcrumbRoutes';
import { findBreadcrumbs, ResolvedBreadcrumb } from 'utils/breadcrumbUtils';
import { useSelector } from 'store';

// assets
import { HomeOutlined } from '@ant-design/icons';

// types
import { OverrideIcon } from 'types/root';

// ==============================|| BREADCRUMBS ||============================== //

export interface BreadCrumbSxProps extends CSSProperties {
  mb?: string;
  bgcolor?: string;
}

interface Props {
  card?: boolean;
  divider?: boolean;
  icon?: boolean;
  icons?: boolean;
  maxItems?: number;
  rightAlign?: boolean;
  separator?: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadCrumbSxProps;
}

const Breadcrumbs = ({ card, divider = true, icon, icons, maxItems, rightAlign, separator, title, titleBottom, sx, ...others }: Props) => {
  const theme = useTheme();
  const location = useLocation();
  const breadcrumbData = useSelector((state) => state.breadcrumb.data);
  const [breadcrumbs, setBreadcrumbs] = useState<ResolvedBreadcrumb[] | null>(null);

  const iconSX = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  useEffect(() => {
    const resolved = findBreadcrumbs(location.pathname, breadcrumbRoutes, breadcrumbData);
    setBreadcrumbs(resolved);
  }, [location.pathname, breadcrumbData]);

  // item separator
  const SeparatorIcon = separator!;
  const separatorIcon = separator ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} /> : '/';

  // No breadcrumbs found for this route
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return <></>;
  }

  // Get last breadcrumb for page title
  const displayTitle = breadcrumbs[breadcrumbs.length - 1].title;

  // Build breadcrumb items
  const breadcrumbItems = breadcrumbs.map((segment, index) => {
    const isLast = index === breadcrumbs.length - 1;

    if (isLast) {
      // Last item is not clickable
      return (
        <Typography key={index} variant="subtitle2" color="textPrimary">
          {segment.title}
        </Typography>
      );
    }

    // Parent items are clickable
    return (
      <Typography
        key={index}
        component={Link}
        to={segment.url || '/'}
        variant="subtitle2"
        sx={{ textDecoration: 'none' }}
        color="textSecondary"
      >
        {segment.title}
      </Typography>
    );
  });

  return (
    <MainCard
      border={card}
      sx={card === false ? { mb: 3, bgcolor: 'transparent', ...sx } : { mb: 3, ...sx }}
      {...others}
      content={card}
      shadow="none"
    >
      <Grid
        container
        direction={rightAlign ? 'row' : 'column'}
        justifyContent={rightAlign ? 'space-between' : 'flex-start'}
        alignItems={rightAlign ? 'center' : 'flex-start'}
        spacing={1}
      >
        {title && !titleBottom && (
          <Grid item>
            <Typography variant="h2">{displayTitle}</Typography>
          </Grid>
        )}
        <Grid item>
          <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
            <Typography component={Link} to="/" color="textSecondary" variant="h6" sx={{ textDecoration: 'none' }}>
              <HomeOutlined style={iconSX} />
            </Typography>
            {breadcrumbItems}
          </MuiBreadcrumbs>
        </Grid>
        {title && titleBottom && (
          <Grid item sx={{ mt: card === false ? 0.25 : 1 }}>
            <Typography variant="h2">{displayTitle}</Typography>
          </Grid>
        )}
      </Grid>
      {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
    </MainCard>
  );
};

export default Breadcrumbs;
